import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { DraggableMachine } from './DraggableMachine';
import { MaquinaParaDragDrop } from '../../../services/asignacionesUsuarioMaquinaService';

// ===============================
// TIPOS
// ===============================

interface MachinePoolProps {
  title: string;
  machines: MaquinaParaDragDrop[];
  dropId: string;
  emptyMessage: string;
  emptyIcon: React.ReactNode;
  isFromPool?: boolean;
  sourceUserId?: number;
  color?: 'blue' | 'green' | 'yellow';
}

// ===============================
// COMPONENTE
// ===============================

export function MachinePool({ 
  title, 
  machines, 
  dropId, 
  emptyMessage, 
  emptyIcon,
  isFromPool = false,
  sourceUserId,
  color = 'blue'
}: MachinePoolProps) {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({
    id: dropId,
    data: {
      type: dropId === 'available-machines' ? 'available-pool' : 'assigned-pool',
      userId: sourceUserId,
    },
  });

  const colorClasses = {
    blue: {
      border: isOver ? 'border-blue-300' : 'border-gray-200',
      bg: isOver ? 'bg-blue-50' : 'bg-white',
      header: 'bg-gray-50',
      icon: 'bg-gray-100 text-gray-600',
      counter: 'bg-gray-100 text-gray-800'
    },
    green: {
      border: isOver ? 'border-green-300' : 'border-gray-200',
      bg: isOver ? 'bg-green-50' : 'bg-white',
      header: 'bg-green-50',
      icon: 'bg-green-100 text-green-600',
      counter: 'bg-green-100 text-green-800'
    },
    yellow: {
      border: isOver ? 'border-yellow-300' : 'border-gray-200',
      bg: isOver ? 'bg-yellow-50' : 'bg-white',
      header: 'bg-yellow-50',
      icon: 'bg-yellow-100 text-yellow-600',
      counter: 'bg-yellow-100 text-yellow-800'
    }
  };

  const classes = colorClasses[color];

  return (
    <div className={`rounded-lg border-2 shadow-sm transition-all duration-200 ${classes.border}`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b border-gray-100 rounded-t-lg ${classes.header}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-6 h-6 rounded flex items-center justify-center ${classes.icon}`}>
              {emptyIcon}
            </div>
            <h3 className="text-sm font-medium text-gray-900">
              {title}
            </h3>
          </div>

          {/* Counter */}
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${classes.counter}`}>
              {machines.length} máquinas
            </span>
          </div>
        </div>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={`p-4 min-h-40 transition-all duration-200 ${classes.bg}`}
      >
        {/* Mensaje cuando está vacío */}
        {machines.length === 0 && !isOver && (
          <div className="flex items-center justify-center h-32 text-gray-400">
            <div className="text-center">
              <div className="text-4xl mb-2">{emptyIcon}</div>
              <p className="text-sm text-gray-500">{emptyMessage}</p>
            </div>
          </div>
        )}

        {/* Indicador de drop activo */}
        {isOver && (
          <div className="flex items-center justify-center h-32 text-blue-600 font-medium">
            <div className="text-center">
              <svg className="mx-auto h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <p className="mt-2 text-sm">{t('admin.drop_here')}</p>
            </div>
          </div>
        )}

        {/* Grid de máquinas */}
        {machines.length > 0 && !isOver && (
          <div className={`grid gap-3 ${machines.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
            {machines.map((maquina) => (
              <DraggableMachine
                key={`${dropId}-machine-${maquina.id}`}
                maquina={maquina}
                isInPool={isFromPool}
                sourceUserId={sourceUserId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer con estadísticas */}
      {machines.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {machines.length} máquina{machines.length !== 1 ? 's' : ''}
            </span>
            <span>
              Tipos: {[...new Set(machines.map(m => m.tipo))].length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
